import React from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Stack,
  Divider,
  useTheme,
} from "@mui/material";
import ImageOrderingEditor, {
  ImageOrderingValue,
} from "../ImageOrderingEditor";
import TagMapEditor from "../TagMapEditor";
import AddTagMapButton from "../AddTagMapButton";
import { suggestionsForMap } from "../standardTags";
import SyndicatorCard from "../SyndicatorCard";

interface Props {
  config: any;
  onChange: (field: string, value: any) => void;
}

const GROUP_SUGGESTIONS = ["exteriorOriginal", "exteriorProcessed", "manual"];
const DEFAULT_TAGMAPS = ["exteriorOriginal_TagMap", "exteriorProcessed_TagMap"];

export const mastercraftDefaults = {
  syndicatorData: {
    rooftop_id: 0,
    client: "",
    rooftop: "",
    groupMaps: {
      manual: { photoNamePrefix: "manual_", groupName: "manual" },
      exteriorOriginal: { groupName: "exterior" },
      exteriorProcessed: { groupName: "enhanced" },
    },
    tagMapping: {
      exteriorOriginal_TagMap: {},
      exteriorProcessed_TagMap: {},
    },
  },
  imageOrdering: {
    ImageOrder: "",
  },
};

const MastercraftForm: React.FC<Props> = ({ config, onChange }) => {
  const theme = useTheme();
  const accent = theme.palette.status.accent;

  const syndicatorData = config.syndicatorData || {};
  const groupMaps = syndicatorData.groupMaps || {};
  const tagMapping = syndicatorData.tagMapping || {};
  const tagMapNames = Array.from(
    new Set([
      ...DEFAULT_TAGMAPS,
      ...Object.keys(tagMapping).filter((k) => k.endsWith("_TagMap")),
    ]),
  );
  const imageOrdering: ImageOrderingValue = config.imageOrdering || {};

  return (
    <SyndicatorCard title="MasterCraft Configuration">
      <Stack spacing={3}>
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1.5, color: accent, fontWeight: 600 }}
          >
            Syndicator Data
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Rooftop ID"
                fullWidth
                size="small"
                type="number"
                value={syndicatorData.rooftop_id ?? 0}
                onChange={(e) =>
                  onChange(
                    "syndicatorData.rooftop_id",
                    parseInt(e.target.value, 10) || 0,
                  )
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Client"
                fullWidth
                size="small"
                value={syndicatorData.client || ""}
                onChange={(e) =>
                  onChange("syndicatorData.client", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Rooftop"
                fullWidth
                size="small"
                value={syndicatorData.rooftop || ""}
                onChange={(e) =>
                  onChange("syndicatorData.rooftop", e.target.value)
                }
              />
            </Grid>
          </Grid>
        </Box>

        <Box>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1.5, color: accent, fontWeight: 600 }}
          >
            Group Maps
          </Typography>
          <Stack spacing={2}>
            {["manual", "exteriorOriginal", "exteriorProcessed"].map(
              (groupName) => {
                const groupConfig = groupMaps[groupName] || {};
                return (
                  <Box key={groupName}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontWeight: 600,
                        display: "block",
                        mb: 0.75,
                        fontFamily: "ui-monospace, Menlo, monospace",
                      }}
                    >
                      {groupName}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="groupName"
                          fullWidth
                          size="small"
                          value={groupConfig.groupName || ""}
                          onChange={(e) =>
                            onChange(
                              `syndicatorData.groupMaps.${groupName}.groupName`,
                              e.target.value,
                            )
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="photoNamePrefix"
                          fullWidth
                          size="small"
                          placeholder="(optional)"
                          value={groupConfig.photoNamePrefix || ""}
                          onChange={(e) =>
                            onChange(
                              `syndicatorData.groupMaps.${groupName}.photoNamePrefix`,
                              e.target.value,
                            )
                          }
                        />
                      </Grid>
                    </Grid>
                  </Box>
                );
              },
            )}
          </Stack>
        </Box>

        <Box>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1.5, color: accent, fontWeight: 600 }}
          >
            Tag Mapping
          </Typography>
          <Stack spacing={2}>
            {tagMapNames.map((mapName) => {
              const isDefault = DEFAULT_TAGMAPS.includes(mapName);
              return (
                <TagMapEditor
                  key={mapName}
                  label={mapName}
                  value={tagMapping[mapName]}
                  onChange={(next) =>
                    onChange(`syndicatorData.tagMapping.${mapName}`, next)
                  }
                  onRemove={
                    isDefault
                      ? undefined
                      : () => {
                          const next = { ...tagMapping };
                          delete next[mapName];
                          onChange("syndicatorData.tagMapping", next);
                        }
                  }
                  keyLabel="Tag"
                  valueLabel="MasterCraft Slot"
                  tagSuggestions={suggestionsForMap(mapName)}
                />
              );
            })}
            <AddTagMapButton
              existingNames={tagMapNames}
              onAdd={(name) =>
                onChange(`syndicatorData.tagMapping.${name}`, {})
              }
            />
          </Stack>
        </Box>

        <Divider />

        <Box>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1.5, color: accent, fontWeight: 600 }}
          >
            Image Ordering
          </Typography>
          <ImageOrderingEditor
            value={imageOrdering}
            onChange={(next) => onChange("imageOrdering", next)}
            groupSuggestions={GROUP_SUGGESTIONS}
          />
        </Box>
      </Stack>
    </SyndicatorCard>
  );
};

export default MastercraftForm;
